import type { PresignedPost, Source } from "../Source";

import { commitSource } from "./commitSource";
import { registerSource } from "./registerSource";

/* global FormData fetch File */

/**
 * 受領データのアップロードサイズ上限 (バイト)。
 * バックエンド config.py の SOURCE_MAX_UPLOAD_BYTES と一致させる（フロント先行チェック。
 * 権威ある enforcement はバックエンド register + S3 policy が担う）。
 */
export const SOURCE_MAX_UPLOAD_BYTES = 15 * 1024 * 1024; // 15 MB

/** 上限超過なら例外を投げる。register より前に呼び、無駄な往復を避ける。 */
export function validateSourceSize(file: File): void {
    if (file.size > SOURCE_MAX_UPLOAD_BYTES) {
        const maxMb = Math.floor(SOURCE_MAX_UPLOAD_BYTES / (1024 * 1024));
        throw new Error(`ファイルサイズが上限 ${maxMb}MB を超えています（${file.name}）`);
    }
}

/**
 * presigned POST で S3 へ実体を直接アップロードする。
 * 認証ヘッダは付与しない（presigned 署名で認可されるため。Bearer/Cookie を足すと署名が壊れる）。
 *
 * 受領データ用に entity 内で自己完結させる（shared 層が無く、entity 間結合を避けるため意図的複製）。
 */
async function putToPresignedPost(upload: PresignedPost, file: File): Promise<void> {
    const form = new FormData();
    for (const [key, value] of Object.entries(upload.fields)) {
        form.append(key, value);
    }
    form.append("file", file);
    const res = await fetch(upload.url, { method: "POST", body: form });
    if (!res.ok) throw new Error(`アップロードに失敗しました (status ${res.status})`);
}

/**
 * register → S3 直接アップロード → commit をまとめて実行する高水準ヘルパー。
 * UI からはこれ 1 つで「受領データを追加」を完結できる。
 */
export async function uploadSource(projectId: string, file: File): Promise<Source> {
    validateSourceSize(file);
    const { source, upload } = await registerSource(projectId, {
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size,
    });
    await putToPresignedPost(upload, file);
    return commitSource(projectId, source.id);
}
